import * as yup from 'yup'
import { useContext, useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'

import useAPIFetch from '@hooks/useAPIFetch'
import Header from '@components/configurations/header'
import UserService from '@services/users/user.service'
import { dirtyValues } from '@utils/filterDirtyFields'
import ValidationToast from '@components/configurations/form/ValidationToast'
import UserInfoForm from '@components/configurations/users_config/users/form/userInfoForm'
import PermissionSetting from '@components/configurations/users_config/users/form/permissionSetting'
import { useMeta } from '@contexts/Meta'
import { UserInfo } from '@models/user.model'
import { isEqual } from 'lodash'
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded'
import { PermissionCodeEnum } from '@models/permission'
import { hasPermission } from '@utils/permission_helper'
import { AuthContext } from '@contexts/Auth'

export const EditUser = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()
	const { enqueueSnackbar } = useSnackbar()
	const { id: profileId } = useParams<string>()
	const { setRequest, isLoading } = useAPIFetch()
	const { state: authState } = useContext(AuthContext)

	const validationSchema = yup
		.object()
		.shape({
			email: yup.string().email().required('Email is required'),
			first_name: yup.string().max(255).trim().required('First Name is required'),
			last_name: yup.string().max(255).trim().required('Last Name is required'),
		})
		.required()

	const methods = useForm<UserInfo>({
		resolver: yupResolver(validationSchema),
		mode: 'all',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
	})
	const {
		reset,
		handleSubmit,
		control,
		formState: { isDirty, dirtyFields },
	} = methods

	useEffect(() => {
		reload()
	}, [profileId])

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const onSubmit = (data: UserInfo) => {
		console.log('data', data)
		console.log(
			'data',
			isEqual(
				data.role_ids,
				data.roles?.map((x) => x.role_id),
			),
		)
		if (
			!isDirty &&
			isEqual(
				data.role_ids,
				data.roles?.map((x) => x.role_id),
			)
		) {
			enqueueSnackbar('You have no update in this session.', { variant: 'warning' })
		} else {
			let new_value: any = dirtyValues(dirtyFields, data)
			let _roles = data.roles?.map((x) => {
				return x.role_id
			})
			let _role_ids = data.role_ids
			let addedRole = _role_ids
				?.filter((old) => _roles?.indexOf(old) === -1)
				?.map((x = '') => {
					return { role_id: x, active: true }
				})
			let deletedRole = _roles
				?.filter((old) => (old === undefined ? false : _role_ids?.indexOf(old) === -1))
				?.map((x = '') => {
					return { role_id: x, active: false }
				})

			let new_roles_ids: { role_id?: string; active: boolean }[] = []
			if (deletedRole && addedRole) {
				new_roles_ids = addedRole?.concat(deletedRole)
			}

			new_value = { ...new_value, roles: new_roles_ids }
			delete new_value.role_ids
			console.info('[Update Users] - payload ', new_value)
			setRequest({
				callback: async (cancelToken: any) => {
					await UserService.UpdateUserProfile(new_value, data?.id ?? '').then(() => {
						enqueueSnackbar('User profile updated.', { variant: 'success' })
						setMeta({ isLoading: false })
						navigate(-1)
					})
				},
			})
		}
	}

	const onError = (errors: any) => {
		console.log('errors', errors)
	}

	const reload = async () => {
		setRequest({
			callback: async () => {
				if (profileId) {
					await UserService.GetUserProfile(profileId)
						.then(async (f) => {
							f.roles = f.role_ids?.map((x) => {
								return {
									role_id: x,
									active: true,
								}
							})
							f.active = f.base_system_field?.active ?? true
							f.selectedInDialog = []
							if (f) reset(f)
						})
						.catch((err) => {
							enqueueSnackbar(err.response.data.message, {
								variant: 'error',
							})
							navigate('/configurations/users_config/users')
						})
				}
			},
		})
	}

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<ArrowBackIosRoundedIcon
					fontSize={'large'}
					sx={{ cursor: 'pointer', color: '#707070' }}
					onClick={() => {
						navigate(`/configurations/users_config/users`)
					}}
				/>
				<Header
					title={t('Edit Users')}
					icon={<PersonIcon sx={{ fontSize: 35, color: '#707070' }} />}
				/>
			</Box>
			<Box component="form" onSubmit={handleSubmit(onSubmit, onError)} sx={{ marginX: '45px' }}>
					<FormProvider {...methods}>
						<UserInfoForm userID={profileId} disableIdentityFields={true} />

					{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserAssignRole]) && (
						<PermissionSetting />
					)}

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							marginTop: '12px',
						}}
					>
						<Button variant="contained" color="defaultButton" type="submit" disabled={isLoading}>
							{t('Confirm')}
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={() => navigate(-1)}
							disabled={isLoading}
						>
							{t('Cancel')}
						</Button>
					</Box>
				</FormProvider>
				<ValidationToast control={control} />
			</Box>
		</>
	)
}
